import React from "react";

/** Minimal inline Markdown renderer for bold/italic/code */
function renderInlineMD(text) {
  if (typeof text !== "string") return text;

  // Split into tokens capturing **bold**, *italic*, `code`
  const tokens = text
    .split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g)
    .filter(Boolean);

  return tokens.map((t, i) => {
    // Bold: **...**
    if (t.startsWith("**") && t.endsWith("**") && t.length > 4) {
      return <strong key={i} className="font-semibold">{t.slice(2, -2)}</strong>;
    }
    // Italic: *...*
    if (t.startsWith("*") && t.endsWith("*") && t.length > 2) {
      return <em key={i}>{t.slice(1, -1)}</em>;
    }
    // Code: `...`
    if (t.startsWith("`") && t.endsWith("`") && t.length > 2) {
      return (
        <code key={i} className="px-1 py-0.5 rounded bg-gray-100">
          {t.slice(1, -1)}
        </code>
      );
    }

    // Default: strip ANY stray asterisks that weren't part of valid tokens
    const cleaned = t.replace(/\*/g, "");
    return <span key={i}>{cleaned}</span>;
  });
}

export default function SmartAnswer({
  value,
className = "w-[100%] whitespace-pre-wrap break-words text-[14px] font-[Outfit] font-medium leading-normal text-[var(--Neutral_2,#52525B)] mt-1",
  showOnlyTables = false,
}) {
  // Try to parse JSON if value is a string
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = value;
    }
  }

  // ---------- Case 0: Array of objects (line items) ----------
  if (
    Array.isArray(parsed) &&
    parsed.length > 0 &&
    typeof parsed[0] === "object" &&
    !Array.isArray(parsed[0])
  ) {
    const headers = Array.from(
      new Set(parsed.flatMap((o) => Object.keys(o || {})))
    );

    return (
      <TableWrapper>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <Th key={i}>{renderInlineMD(h)}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsed.map((row, rIdx) => (
            <tr key={rIdx}>
              {headers.map((h, cIdx) => (
                <Td key={cIdx}>{renderInlineMD((row || {})[h] ?? "—")}</Td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableWrapper>
    );
  }

  // ---------- Case 2: Markdown table ----------
  if (typeof value === "string" && value.includes("|") && value.split("\n").length > 2) {
    const lines = value.trim().split("\n").filter(Boolean);

    // Basic markdown table check: second line has --- divider
    const hasDivider = !!lines[1] && lines[1].includes("---");
    if (hasDivider) {
      const headers = lines[0].split("|").map((s) => s.trim()).filter(Boolean);
      const rows = lines.slice(2).map((line) =>
        line.split("|").map((s) => s.trim()).filter(Boolean)
      );

      return (
        <TableWrapper>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <Th key={i}>{renderInlineMD(h)}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <Td key={j}>{renderInlineMD(cell)}</Td>
                ))}
              </tr>
            ))}
          </tbody>
        </TableWrapper>
      );
    }
  }

  // ---------- Case 3: Mixed paragraph + key: value ----------
  if (typeof value === "string") {
    const lines = value.split("\n");
    const groups = [];
    let p = [];
    let t = [];

    const flush = () => {
      if (p.length || t.length) {
        groups.push({ paragraph: p, table: t });
        p = [];
        t = [];
      }
    };

    for (const raw of lines) {
      const line = raw || "";
      if (!line.trim()) continue;
      const idx = line.indexOf(":");
      const key = idx >= 0 ? line.slice(0, idx).trim() : "";
      const val = idx >= 0 ? line.slice(idx + 1).trim() : "";

      if (idx > 0 && key.length < 50 && line.length < 150 && val) {
        t.push([key, val]);
      } else {
        flush();
        p.push(line);
      }
    }
    flush();

    const tableBlocks = groups.filter((g) => g.table.length > 0);

    if (showOnlyTables) {
      if (tableBlocks.length > 0) {
        // Render only the table parts
        return (
          <div className={className}>
            {tableBlocks.map((g, i) => (
              <TableWrapper key={i}>
                <thead>
                  {/* <tr>
                    <Th>Field</Th>
                    <Th>Value</Th>
                  </tr> */}
                </thead>
                <tbody>
                  {g.table.map(([k, v], j) => (
                    <tr key={j}>
                      <Td className="font-semibold w-1/3">{renderInlineMD(k)}</Td>
                      <Td>{renderInlineMD(v)}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrapper>
            ))}
          </div>
        );
      } else {
        // ✅ No table found: KEEP the normal sentence (do NOT remove)
        return (
          <p className={className}>
            {value !== undefined && value !== null ? String(value).trim() : ""}
          </p>
        );
      }
    }

    // Default: paragraphs + table in groups
    return (
      <div className={className}>
        {groups.map((g, idx) => (
          <div key={idx} className="mb-3">
            {g.paragraph.map((line, i) => (
              <p key={i} className="mb-1">
                {renderInlineMD(line)}
              </p>
            ))}
            {g.table.length > 0 && (
              <TableWrapper>
                <thead>
                  <tr>
                    <Th>Field</Th>
                    <Th>Value</Th>
                  </tr>
                </thead>
                <tbody>
                  {g.table.map(([k, v], i) => (
                    <tr key={i}>
                      <Td className="font-semibold w-1/3">{renderInlineMD(k)}</Td>
                      <Td>{renderInlineMD(v)}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrapper>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ---------- Fallback ----------
  return (
    <p className={className}>
      {value !== undefined && value !== null ? String(value).trim() : ""}
    </p>
  );
}

/* ---------- Tiny UI helpers ---------- */
function TableWrapper({ children }) {
  return (
    <table className="w-[100%] mt-2 border border-gray-200  text-sm mb-2">
      {children}
    </table>
  );
}
function Th({ children, className = "" }) {
  return (
    <th
      className={`border border-gray-300 px-3 py-2 bg-gray-100 text-left whitespace-pre-wrap ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`border border-gray-300 px-3 py-2 whitespace-pre-wrap ${className}`}>
      {children}
    </td>
  );
}
